import { Component, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, Navigate, useLocation, useNavigate, useParams } from "react-router";
import { ConfirmButton } from "@/components/common/ConfirmButton";
import { DisclosureChevron, ExpandIn } from "@/components/common/Disclosure";
import { getDocsPages, type DocsPageContent } from "@/lib/api";
import { ACTION_LINK_CLASS } from "@/lib/link-styles";
import { DocPage } from "./DocElements";
import { DocMarkdown } from "./DocMarkdown";
import { DocsPageToc } from "./DocsPageToc";
import { useCurrentHeading } from "./useCurrentHeading";
import { useDocHeadings } from "./useDocHeadings";

// ガイドの文章はサインインした人だけに見せるため、アプリの JS に入れず API から取る
export function DocsPage() {
	const { page: pageParam } = useParams<{ page: string }>();
	const [pages, setPages] = useState<DocsPageContent[] | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [attempt, setAttempt] = useState(0);

	useEffect(() => {
		const controller = new AbortController();
		getDocsPages(controller.signal)
			.then(setPages)
			.catch((err: Error) => {
				if (controller.signal.aborted) return;
				console.error("利用ガイドの取得に失敗しました", err);
				setError(err.message);
			});
		return () => controller.abort();
	}, [attempt]);

	if (error !== null) {
		return (
			<DocsStatus>
				<p className="m-0 text-ui text-danger">{error}</p>
				<ConfirmButton
					label="再試行"
					loading={false}
					variant="secondary"
					size="sm"
					onClick={() => {
						setError(null);
						setAttempt((n) => n + 1);
					}}
					data-testid="docs-retry"
				/>
			</DocsStatus>
		);
	}
	if (pages === null) {
		return (
			<DocsStatus>
				<p className="m-0 text-ui text-fg-3">読み込み中…</p>
			</DocsStatus>
		);
	}
	if (pages.length === 0) {
		return (
			<DocsStatus>
				<p className="m-0 text-ui text-danger">利用ガイドのページがありません</p>
			</DocsStatus>
		);
	}
	const page = pages.find((p) => p.slug === pageParam);
	if (!page) return <Navigate to={`/docs/${pages[0].slug}`} replace />;
	return <DocsLayout pages={pages} page={page} />;
}

function DocsStatus({ children }: { children: React.ReactNode }) {
	return (
		<div
			className="flex items-center justify-center gap-2"
			style={{ height: "100vh", background: "var(--surface-1)" }}
		>
			{children}
		</div>
	);
}

function DocsSection({ page, active }: { page: DocsPageContent; active: boolean }) {
	return (
		<DocPage title={page.label}>
			<DocsSectionBoundary>
				<DocMarkdown markdown={page.markdown} active={active} />
			</DocsSectionBoundary>
		</DocPage>
	);
}

/** 1 ページの Markdown を描けなくても、ほかのページは読めるようにする (backend の本文が先に新しい書き方を使ったときなど)。 */
class DocsSectionBoundary extends Component<{ children: React.ReactNode }, { failed: boolean }> {
	state = { failed: false };

	static getDerivedStateFromError() {
		return { failed: true };
	}

	componentDidCatch(error: unknown) {
		console.error("利用ガイドのページを表示できませんでした", error);
	}

	render() {
		if (!this.state.failed) return this.props.children;
		return (
			<p className="m-0 text-ui text-danger" data-testid="docs-section-error">
				このページを表示できませんでした。アプリを再読み込みしてください。
			</p>
		);
	}
}

function DocsLayout({ pages, page }: { pages: DocsPageContent[]; page: DocsPageContent }) {
	const sectionRefs = useRef<Record<string, HTMLElement | null>>({});
	const [headingsBySlug, setHeadingsBySlug] = useState<Record<string, HTMLHeadingElement[]>>({});
	const [openSlugs, setOpenSlugs] = useState<ReadonlySet<string>>(() => new Set([page.slug]));
	// 最初の表示で開いていたページは、閉じるまでアニメーションなしで出す
	const [initialOpenSlug, setInitialOpenSlug] = useState<string | null>(page.slug);
	const [pendingHeading, setPendingHeading] = useState<HTMLHeadingElement | null>(null);

	// ページ名以外の手段で移ったときも、移った先を同じレンダーで開いてちらつかせない
	const [prevSlug, setPrevSlug] = useState(page.slug);
	if (prevSlug !== page.slug) {
		setPrevSlug(page.slug);
		if (!openSlugs.has(page.slug)) setOpenSlugs(new Set(openSlugs).add(page.slug));
	}

	// 見出しはページ本文から拾い、ナビの目次をページごとに書かずに済ませる。各ページの h2 は静的
	useLayoutEffect(() => {
		setHeadingsBySlug(
			Object.fromEntries(
				pages.map((p) => [
					p.slug,
					Array.from(sectionRefs.current[p.slug]?.querySelectorAll("h2") ?? []),
				]),
			),
		);
	}, [pages]);

	const [main, setMain] = useState<HTMLElement | null>(null);
	const [activeSection, setActiveSection] = useState<HTMLElement | null>(null);
	useLayoutEffect(() => {
		setActiveSection(sectionRefs.current[page.slug] ?? null);
	}, [page.slug]);
	const pageHeadings = useDocHeadings(activeSection);
	const [currentHeading, scrollToTarget] = useCurrentHeading(main, pageHeadings, page.slug);

	// 同じハッシュのリンクを押し直しても飛べるよう、location.key ごとにスクロールする
	const { hash, key } = useLocation();
	useEffect(() => {
		if (!hash) return;
		const target = sectionRefs.current[page.slug]?.querySelector(
			`#${CSS.escape(decodeURIComponent(hash.slice(1)))}`,
		);
		if (target) scrollToTarget(target);
	}, [hash, key, page.slug, scrollToTarget]);

	// 隠れているページの見出しは、そのページが表示されてからスクロールする
	useEffect(() => {
		if (!pendingHeading || !sectionRefs.current[page.slug]?.contains(pendingHeading)) return;
		scrollToTarget(pendingHeading);
		setPendingHeading(null);
	}, [pendingHeading, page.slug, scrollToTarget]);

	const navigate = useNavigate();
	const toggle = (slug: string) => {
		const next = new Set(openSlugs);
		if (next.delete(slug)) {
			if (slug === initialOpenSlug) setInitialOpenSlug(null);
		} else {
			next.add(slug);
		}
		setOpenSlugs(next);
	};
	const goToHeading = (slug: string, heading: HTMLHeadingElement) => {
		if (slug === page.slug) {
			scrollToTarget(heading);
			return;
		}
		setPendingHeading(heading);
		void navigate(`/docs/${slug}`);
	};

	return (
		<div
			style={{
				display: "grid",
				gridTemplateColumns: "200px 1fr",
				height: "100vh",
				background: "var(--surface-1)",
			}}
		>
			<nav
				style={{
					borderRight: "var(--pane-header-border)",
					padding: "12px 8px",
					display: "flex",
					flexDirection: "column",
					gap: 2,
					minHeight: 0,
				}}
			>
				<div
					style={{
						display: "flex",
						alignItems: "center",
						height: 40,
						padding: "0 10px",
						marginBottom: 4,
					}}
				>
					<h1
						style={{
							margin: 0,
							fontSize: "var(--text-ui)",
							fontWeight: 600,
							letterSpacing: "var(--tracking-snug)",
							color: "var(--fg-1)",
						}}
					>
						利用ガイド
					</h1>
				</div>

				<div className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto">
					{pages.map((p) => {
						const active = p.slug === page.slug;
						const open = openSlugs.has(p.slug);
						return (
							<div key={p.slug} className="flex shrink-0 flex-col gap-0.5">
								{/* 別のページなら移って開く (開くのは slug の変化を見て行う)。表示中のページなら開閉だけ */}
								<Link
									to={`/docs/${p.slug}`}
									onClick={(e) => {
										if (!active) return;
										e.preventDefault();
										toggle(p.slug);
									}}
									data-testid={`docs-nav-page-${p.slug}`}
									className={`flex h-8 shrink-0 items-center gap-1 rounded-3 pr-2.5 pl-2.5 text-ui no-underline hover:text-fg-0 ${active ? "bg-surface-2 font-semibold text-fg-1" : "text-fg-2"}`}
								>
									<DisclosureChevron open={open} size={12} />
									<span className="min-w-0 truncate">{p.label}</span>
								</Link>
								{open && (
									<ExpandIn animate={p.slug !== initialOpenSlug}>
										<div className="flex flex-col gap-0.5">
											{(headingsBySlug[p.slug] ?? []).map((heading, i) => (
												<button
													key={i}
													type="button"
													onClick={() => goToHeading(p.slug, heading)}
													title={heading.textContent ?? undefined}
													data-tooltip-if-truncated
													className="block h-7 w-full min-w-0 shrink-0 cursor-pointer truncate border-0 bg-transparent pr-2.5 pl-6.5 text-left text-list leading-7 text-fg-3 hover:text-fg-1"
												>
													{heading.textContent}
												</button>
											))}
										</div>
									</ExpandIn>
								)}
							</div>
						);
					})}
				</div>

				<Link
					to="/"
					data-testid="docs-open-app"
					className={`flex h-8 shrink-0 items-center justify-center ${ACTION_LINK_CLASS}`}
				>
					アプリに移動
				</Link>
			</nav>

			<main ref={setMain} style={{ overflow: "auto" }}>
				<div className="flex items-start gap-12 px-12 py-8">
					<article className="min-w-0 flex-1 select-text" style={{ maxWidth: 760 }}>
						{pages.map((p) => (
							<section
								key={p.slug}
								ref={(el) => {
									sectionRefs.current[p.slug] = el;
								}}
								hidden={p.slug !== page.slug}
							>
								<DocsSection page={p} active={p.slug === page.slug} />
							</section>
						))}
					</article>
					<DocsPageToc
						headings={pageHeadings}
						currentIndex={currentHeading}
						onSelect={scrollToTarget}
					/>
				</div>
			</main>
		</div>
	);
}
